import type { Notification, NotificationTab } from '../types/notification.types';

/**
 * N5 (86d3w2kg2) — the store's merge rules, as pure functions.
 *
 * Kept out of the zustand store deliberately: vitest runs in Node with no jsdom, so anything worth
 * asserting has to be callable without React. This is also the E9 precedent (`activity/lib/*.ts`).
 */

/**
 * THE ordering, bit-identical to the server's `ORDER BY (CreatedAt DESC, Id DESC)`.
 *
 * ⛔ Integer math only. Two traps this closes: comparing `createdAt` STRINGS sorts `"…:05Z"` after
 * `"…:05.5Z"` because `System.Text.Json` trims trailing fractional zeros and `'Z'` > `'.'`; and
 * `Date.parse` truncates to milliseconds while the server orders at microseconds, so rows inside one
 * millisecond compare equal and shuffle on every render.
 */
export function compareDesc(a: Notification, b: Notification): number {
  if (a.createdAtMicros !== b.createdAtMicros) return b.createdAtMicros - a.createdAtMicros;
  return b.id - a.id;
}

/**
 * Mirrors the repository's `ApplyTab` switch EXACTLY. If these drift, a row appears in one surface
 * and not the other, and nothing fails.
 *
 * Note `done` is an orthogonal axis rather than a fourth state — every other tab excludes it — and
 * that `unread` deliberately INCLUDES needs-action rows: a failed posting nobody has looked at is
 * unread by any honest reading.
 */
export function matchesTab(n: Notification, tab: NotificationTab): boolean {
  switch (tab) {
    case 'needs':
      return !n.done && n.needsAction && !n.resolved;
    case 'unread':
      return !n.done && n.unread;
    case 'done':
      return n.done;
    default:
      return !n.done;
  }
}

/** The pinned predicate — the same one the server's partial index carries. */
export function isPinned(n: Notification): boolean {
  return !n.done && n.needsAction && !n.resolved;
}

/** Inserts into an already-sorted array, keeping it sorted. Returns a NEW array. */
function insertSorted(rows: Notification[], row: Notification): Notification[] {
  const next = rows.slice();
  let lo = 0;
  let hi = next.length;

  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (compareDesc(next[mid], row) < 0) lo = mid + 1;
    else hi = mid;
  }

  next.splice(lo, 0, row);
  return next;
}

/**
 * Folds one row into the paged window, gating membership in BOTH directions (house rule 86d3bujyr).
 *
 * A row that no longer matches the tab is REMOVED, not just skipped — otherwise marking something
 * done leaves it sitting in the "all" list until the next fetch. A row that newly matches is
 * inserted in sorted position rather than appended.
 *
 * ⚠️ `userInitiated` exists for exactly one mutation. A row that sorts BELOW the loaded window is
 * normally dropped — inserting it would render a phantom detached from unloaded neighbours. But
 * `markUnread` is the one action that inserts INTO a keyset window, and without the bypass the row
 * silently vanishes while the badge increments: the user clicks and sees nothing happen.
 *
 * ⛔ Never touches `nextCursor` or `hasMore`. Those describe a position in a value-keyed sequence;
 * morphing or removing a row cannot move it.
 */
export function upsertInto(
  items: Notification[],
  row: Notification,
  tab: NotificationTab,
  hasMore: boolean,
  userInitiated = false,
): Notification[] {
  const index = items.findIndex((n) => n.id === row.id);
  const belongs = matchesTab(row, tab);

  if (index >= 0) {
    if (!belongs) return items.filter((n) => n.id !== row.id);

    // Replace in place. The sort key is frozen at creation, so the position cannot have moved — but
    // the object identity MUST change or the React Compiler will not re-render the morphing row.
    const next = items.slice();
    next[index] = row;
    return next;
  }

  if (!belongs) return items;

  // Below the loaded window and more pages exist — it belongs to a page nobody has asked for.
  const tail = items[items.length - 1];
  if (!userInitiated && hasMore && tail && compareDesc(row, tail) > 0) return items;

  return insertSorted(items, row);
}

/**
 * Maintains the pinned set INDEPENDENTLY of the paged window — a needs-action failure from three
 * weeks ago must stay visible, and cannot be derived from a recency window.
 */
export function upsertPinned(pinned: Notification[], row: Notification): Notification[] {
  const index = pinned.findIndex((n) => n.id === row.id);

  if (!isPinned(row)) return index >= 0 ? pinned.filter((n) => n.id !== row.id) : pinned;

  if (index >= 0) {
    const next = pinned.slice();
    next[index] = row;
    return next;
  }

  return insertSorted(pinned, row);
}

/**
 * Folds N rows in ONE pass, for the batched `notifications.changed` frame.
 *
 * ⛔ Not a loop over `upsertInto` at the store level. N9's reconciler repairs many rows for one user
 * at once after an outage, and looping would produce N array identities and N badge repaints for a
 * single server event — precisely what the batched frame exists to avoid.
 */
export function foldBatch(
  items: Notification[],
  rows: Notification[],
  tab: NotificationTab,
  hasMore: boolean,
): Notification[] {
  return rows.reduce((acc, row) => upsertInto(acc, row, tab, hasMore), items);
}

export function foldBatchPinned(pinned: Notification[], rows: Notification[]): Notification[] {
  return rows.reduce((acc, row) => upsertPinned(acc, row), pinned);
}

/**
 * Is the incoming page contiguous with the window we already hold?
 *
 * A keyset page is only appendable if its head is strictly older than our tail. If it is not, the
 * window was replaced underneath the request — appending would interleave two unrelated slices and
 * leave a permanently unreachable hole.
 */
export function isContiguous(tail: Notification | undefined, head: Notification | undefined): boolean {
  if (!tail || !head) return true;
  return compareDesc(head, tail) > 0;
}
